import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ClipboardDocumentListIcon,
  LinkIcon,
  UsersIcon,
  QuestionMarkCircleIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentIcon,
  XCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import {
  getAssessment,
  publishAssessment,
  closeAssessment,
  duplicateAssessment,
  archiveAssessment,
  getLinks,
  createLink,
  disableLink,
  getQuestions,
  addQuestions,
} from '../../api/assessmentApi';
import { listQuestions } from '../../api/questionApi';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';

const STATUS_VARIANT = {
  draft:     'default',
  published: 'success',
  closed:    'warning',
  archived:  'error',
};

const TABS = [
  { key: 'overview',     label: 'Overview',     icon: ClipboardDocumentListIcon },
  { key: 'questions',    label: 'Questions',     icon: QuestionMarkCircleIcon },
  { key: 'links',        label: 'Links',         icon: LinkIcon },
  { key: 'participants', label: 'Participants',  icon: UsersIcon },
];

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-6 py-3 border-b border-neutral-100 last:border-0">
      <dt className="w-44 shrink-0 text-sm font-medium text-neutral-500">{label}</dt>
      <dd className="mt-1 sm:mt-0 text-sm text-neutral-900">{value ?? '—'}</dd>
    </div>
  );
}

function AddQuestionsModal({ assessmentId, onAdded, onClose }) {
  const [library, setLibrary] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listQuestions({ per_page: 100, search: search || undefined })
      .then((res) => setLibrary(res.data?.data ?? res.data ?? []))
      .catch(() => toast.error('Failed to load questions'))
      .finally(() => setLoading(false));
  }, [search]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAdd = async () => {
    if (!selected.length) { toast.error('Select at least one question'); return; }
    setSaving(true);
    try {
      await addQuestions(assessmentId, { questions: selected.map((qid) => ({ question_id: qid })) });
      toast.success('Questions added');
      onAdded();
      onClose();
    } catch {
      toast.error('Failed to add questions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="text"
        placeholder="Search questions…"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setLoading(true); }}
        className="input-base text-sm"
      />
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" className="text-primary-600" />
        </div>
      ) : !library.length ? (
        <p className="text-sm text-neutral-500 py-4 text-center">No questions found.</p>
      ) : (
        <ul className="max-h-72 overflow-y-auto divide-y divide-neutral-100 rounded-lg border border-neutral-200">
          {library.map((q) => {
            const qid = q.question_id ?? q.id;
            return (
            <li key={qid} className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors">
              <input
                type="checkbox"
                id={`q-${qid}`}
                checked={selected.includes(qid)}
                onChange={() => toggle(qid)}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor={`q-${qid}`} className="flex-1 cursor-pointer">
                <span className="block text-sm text-neutral-900">{q.text ?? q.question_text ?? q.content}</span>
                {q.question_type && (
                  <span className="text-xs text-neutral-400 mt-0.5 block capitalize">{q.question_type.replace('_', ' ')}</span>
                )}
              </label>
            </li>
          );})}
        </ul>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className="btn-secondary text-sm" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn-primary text-sm" onClick={handleAdd} disabled={saving}>
          {saving ? <Spinner size="sm" /> : null}
          Add Selected ({selected.length})
        </button>
      </div>
    </div>
  );
}

function CreateLinkModal({ assessmentId, onCreated, onClose }) {
  const [form, setForm] = useState({ expires_at: '', max_attempts: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (form.expires_at) payload.expires_at = form.expires_at;
      if (form.max_attempts) payload.max_attempts = Number(form.max_attempts);
      const res = await createLink(assessmentId, payload);
      onCreated(res.data?.data ?? res.data);
      onClose();
    } catch {
      toast.error('Failed to create link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label-base">Expires At (optional)</label>
        <input
          type="datetime-local"
          value={form.expires_at}
          onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          className="input-base text-sm"
        />
      </div>
      <div>
        <label className="label-base">Max Attempts (optional)</label>
        <input
          type="number"
          min={1}
          value={form.max_attempts}
          onChange={(e) => setForm({ ...form, max_attempts: e.target.value })}
          className="input-base text-sm"
          placeholder="Unlimited"
        />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className="btn-secondary text-sm" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn-primary text-sm" disabled={saving}>
          {saving ? <Spinner size="sm" /> : null}
          Create Link
        </button>
      </div>
    </form>
  );
}

export default function AssessmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [links, setLinks] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false);
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);

  const loadAssessment = useCallback(() => {
    return getAssessment(id).then((res) => setAssessment(res.data?.data ?? res.data));
  }, [id]);

  const loadQuestions = useCallback(() => {
    return getQuestions(id).then((res) => setQuestions(res.data?.data ?? res.data ?? []));
  }, [id]);

  const loadLinks = useCallback(() => {
    return getLinks(id).then((res) => setLinks(res.data?.data ?? res.data ?? []));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAssessment(), loadQuestions(), loadLinks()])
      .catch(() => setError('Failed to load assessment'))
      .finally(() => setLoading(false));
  }, [loadAssessment, loadQuestions, loadLinks]);

  const withAction = async (fn, successMsg) => {
    setActionLoading(true);
    try {
      await fn();
      toast.success(successMsg);
      await loadAssessment();
    } catch {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish  = () => withAction(() => publishAssessment(id),  'Assessment published');
  const handleClose    = () => withAction(() => closeAssessment(id),    'Assessment closed');
  const handleDuplicate = async () => {
    setActionLoading(true);
    try {
      const res = await duplicateAssessment(id);
      toast.success('Assessment duplicated');
      const newId = res.data?.data?.id;
      if (newId) navigate(`/assessments/${newId}`);
    } catch {
      toast.error('Failed to duplicate');
    } finally {
      setActionLoading(false);
    }
  };
  const handleArchive  = () => withAction(() => archiveAssessment(id), 'Assessment archived').then(() => navigate('/assessments'));

  const handleDisableLink = async (lid) => {
    try {
      await disableLink(lid);
      toast.success('Link disabled');
      await loadLinks();
    } catch {
      toast.error('Failed to disable link');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!')).catch(() => toast.error('Failed to copy'));
  };

  const getLinkUrl = (token) => `${window.location.origin}/a/${token}`;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="xl" className="text-primary-600" />
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="p-6 text-center">
        <p className="text-error-600 mb-4">{error ?? 'Assessment not found'}</p>
        <Link to="/assessments" className="btn-secondary text-sm">← Back to Assessments</Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Add Questions Modal */}
      {showAddQuestionsModal && (
        <Modal
          isOpen={showAddQuestionsModal}
          onClose={() => setShowAddQuestionsModal(false)}
          title="Add Questions from Library"
          size="lg"
        >
          <AddQuestionsModal
            assessmentId={id}
            onAdded={loadQuestions}
            onClose={() => setShowAddQuestionsModal(false)}
          />
        </Modal>
      )}

      {/* Create Link Modal */}
      {showCreateLinkModal && (
        <Modal
          isOpen={showCreateLinkModal}
          onClose={() => setShowCreateLinkModal(false)}
          title="Create Share Link"
        >
          <CreateLinkModal
            assessmentId={id}
            onCreated={(link) => { setLinks((prev) => [link, ...prev]); }}
            onClose={() => setShowCreateLinkModal(false)}
          />
        </Modal>
      )}

      {/* Back + title */}
      <div>
        <Link
          to="/assessments"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Assessments
        </Link>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-neutral-900 font-heading">{assessment.title}</h1>
            <Badge variant={STATUS_VARIANT[assessment.status] ?? 'default'}>
              {assessment.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {assessment.status === 'draft' && (
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={handlePublish}
                disabled={actionLoading}
              >
                Publish
              </button>
            )}
            {assessment.status === 'published' && (
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={handleClose}
                disabled={actionLoading}
              >
                Close
              </button>
            )}
            <Link to={`/assessments/${id}/edit`} className="btn-secondary text-sm">
              Edit
            </Link>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={handleDuplicate}
              disabled={actionLoading}
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              Duplicate
            </button>
            {assessment.status !== 'archived' && (
              <button
                type="button"
                className="btn-danger text-sm"
                onClick={handleArchive}
                disabled={actionLoading}
              >
                Archive
              </button>
            )}
          </div>
        </div>
        {assessment.description && (
          <p className="mt-2 text-sm text-neutral-500 max-w-2xl">{assessment.description}</p>
        )}
      </div>

      {/* Tab nav */}
      <div className="border-b border-neutral-200">
        <nav className="flex gap-0 -mb-px overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={clsx(
                'inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                activeTab === key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && (
        <Card title="Assessment Details">
          <dl>
            <InfoRow label="Title" value={assessment.title} />
            <InfoRow label="Description" value={assessment.description} />
            <InfoRow label="Status" value={<Badge variant={STATUS_VARIANT[assessment.status] ?? 'default'}>{assessment.status}</Badge>} />
            <InfoRow label="Bootcamp ID" value={assessment.bootcamp_id} />
            <InfoRow
              label="Time Limit"
              value={assessment.time_limit_minutes ? `${assessment.time_limit_minutes} minutes` : 'None'}
            />
            <InfoRow label="Max Attempts" value={assessment.max_attempts ?? 'Unlimited'} />
            <InfoRow label="Anonymous" value={assessment.is_anonymous ? 'Yes' : 'No'} />
            <InfoRow
              label="Created"
              value={
                assessment.created_at
                  ? new Date(assessment.created_at).toLocaleString()
                  : '—'
              }
            />
            <InfoRow
              label="Updated"
              value={
                assessment.updated_at
                  ? new Date(assessment.updated_at).toLocaleString()
                  : '—'
              }
            />
          </dl>
        </Card>
      )}

      {/* Questions tab */}
      {activeTab === 'questions' && (
        <Card
          title={`Questions (${questions.length})`}
          actions={
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => setShowAddQuestionsModal(true)}
            >
              <PlusIcon className="h-4 w-4" />
              Add Questions
            </button>
          }
        >
          {questions.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500">
              No questions added yet.{' '}
              <button
                type="button"
                className="text-primary-600 hover:underline font-medium"
                onClick={() => setShowAddQuestionsModal(true)}
              >
                Add from library
              </button>
              .
            </div>
          ) : (
            <ol className="divide-y divide-neutral-100 -mx-6 -mb-4">
              {questions.map((q, idx) => (
                <li key={q.question_id ?? q.id} className="flex items-start gap-4 px-6 py-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                    {q.order ?? idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-neutral-900">
                      {q.text ?? q.question_text ?? q.content}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      {q.question_type && (
                        <span className="text-xs text-neutral-400 capitalize">
                          {q.question_type.replace('_', ' ')}
                        </span>
                      )}
                      {q.is_required && (
                        <Badge variant="error">Required</Badge>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      )}

      {/* Links tab */}
      {activeTab === 'links' && (
        <Card
          title={`Share Links (${links.length})`}
          actions={
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => setShowCreateLinkModal(true)}
            >
              <PlusIcon className="h-4 w-4" />
              Create Link
            </button>
          }
        >
          {links.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500">
              No share links yet.{' '}
              <button
                type="button"
                className="text-primary-600 hover:underline font-medium"
                onClick={() => setShowCreateLinkModal(true)}
              >
                Create one
              </button>
              .
            </div>
          ) : (
            <div className="-mx-6 -mb-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-100">
                <thead>
                  <tr className="bg-neutral-50">
                    {['URL', 'Expires', 'Max Attempts', 'Usage', 'Status', ''].map((h, i) => (
                      <th
                        key={i}
                        className={clsx(
                          'py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide',
                          i === 0 ? 'px-6 text-left' : 'px-4 text-left',
                          i === 5 ? 'pr-6 text-right' : ''
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50 bg-white">
                  {links.map((lk) => (
                    <tr key={lk.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-3.5 max-w-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-neutral-600 font-mono truncate max-w-[200px]">
                            {getLinkUrl(lk.public_token)}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(getLinkUrl(lk.public_token))}
                            className="shrink-0 text-neutral-400 hover:text-primary-600 transition-colors"
                            title="Copy link"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-neutral-500">
                        {lk.expires_at
                          ? new Date(lk.expires_at).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-neutral-600">
                        {lk.max_attempts ?? 'Unlimited'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-neutral-600">
                        {lk.usage_count ?? lk.use_count ?? 0}
                      </td>
                      <td className="px-4 py-3.5">
                        <Badge variant={lk.is_active === false || lk.disabled_at ? 'error' : 'success'}>
                          {lk.is_active === false || lk.disabled_at ? 'Disabled' : 'Active'}
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        {lk.is_active !== false && !lk.disabled_at && (
                          <button
                            type="button"
                            onClick={() => handleDisableLink(lk.id)}
                            className="btn-ghost text-sm text-error-600 hover:text-error-700 p-1.5"
                            title="Disable link"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Participants tab */}
      {activeTab === 'participants' && (
        <Card title="Participants">
          <div className="py-8 text-center">
            <p className="text-sm text-neutral-500 mb-4">
              View all participants who completed this assessment.
            </p>
            <Link
              to={`/assessments/${id}/participants`}
              className="btn-primary text-sm"
            >
              <UsersIcon className="h-4 w-4" />
              View Participants
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}
